import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axiosInstance from "../api/api";
import BookingEventDetails from "../components/bookings/BookingEventDetails";
import BookingDetailsCard from "../components/bookings/BookingDetailsCard";
import ProposalCard from "../components/Proposal/ProposalCard";
import ProposalDetailedModal from "../components/Proposal/ProposalDetailedModal";
import { useToast } from "../context/ToastContext";
import { isEventClosed } from "../utils/eventDateUtils";
import { capitalizeFirstLetter } from "../utils/stringUtils";
import { updateProposalStatus } from "../api/proposal";

export default function BookingOverview({ eventId: propEventId, isSidebarMode = false, onBack }) {
  const { eventId: urlEventId } = useParams();
  const eventId = propEventId || urlEventId;
  const navigate = useNavigate();
  const { showError } = useToast();
  
  const [bookingDetails, setBookingDetails] = useState(null);
  const [proposals, setProposals] = useState([]);
  
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [detailsError, setDetailsError] = useState("");
  const [error, setError] = useState("");
  
  const [activeTab, setActiveTab] = useState("booked");
  const [hasUserSelectedTab, setHasUserSelectedTab] = useState(false);
  const [selectedProposalIds, setSelectedProposalIds] = useState(() => {
    try {
      const stored = sessionStorage.getItem(`selected_proposals_${eventId}`);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  const [selectedProposalForModal, setSelectedProposalForModal] = useState(null);

  const role = localStorage.getItem("role") || "";
  const isProfessional = role === "professional";

  // Determine if event is closed/expired (active throughout full end date until local midnight)
  const effectiveEvent = eventData || proposals[0]?.eventId || null;


  const isClosed = isEventClosed(effectiveEvent);

  // Proposal classification helpers
  const isProposalCancelled = (p) => {
    if (!p) return false;
    const statusStr = String(p?.status || "").toLowerCase();
    const lifecycleStr = String(
      p?.lifecycleStatus ||
      p?.bookingDoc?.lifecycleStatus ||
      (typeof p?.bookingId === "object" ? p?.bookingId?.lifecycleStatus : "") ||
      ""
    ).toUpperCase();
    const bookingStatusStr = String(
      p?.bookingStatus ||
      p?.bookingDoc?.bookingStatus ||
      (typeof p?.bookingId === "object" ? p?.bookingId?.bookingStatus : "") ||
      ""
    ).toUpperCase();

    return Boolean(
      p?.isCancelled ||
      statusStr === "cancelled" ||
      statusStr === "rejected" ||
      statusStr === "withdrawn" ||
      lifecycleStr === "CANCELLED" ||
      bookingStatusStr === "CANCELLED"
    );
  };

  const isProposalBooked = (p) => {
    if (!p || isProposalCancelled(p)) return false;
    const statusStr = String(p?.status || "").toLowerCase();
    return Boolean(
      p?.isBooked ||
      statusStr === "booked" ||
      Boolean(p?.bookingId || p?.bookingDoc)
    );
  };

  const bookedProposals = proposals.filter((p) => isProposalBooked(p));
  const archivedProposals = proposals.filter((p) => isProposalCancelled(p));
  const activeProposals = proposals.filter((p) => !isProposalBooked(p) && !isProposalCancelled(p));

  // Sync selectedProposalIds to sessionStorage
  useEffect(() => {
    if (!eventId) return;
    if (selectedProposalIds.length > 0) {
      sessionStorage.setItem(`selected_proposals_${eventId}`, JSON.stringify(selectedProposalIds));
    } else {
      sessionStorage.removeItem(`selected_proposals_${eventId}`);
    }
  }, [selectedProposalIds, eventId]);

  // Set default active tab based on proposal counts if user hasn't explicitly selected
  useEffect(() => {
    if (!hasUserSelectedTab && proposals.length > 0) {
      if (bookedProposals.length > 0) {
        setActiveTab("booked");
      } else if (activeProposals.length > 0) {
        setActiveTab("active");
      } else if (archivedProposals.length > 0) {
        setActiveTab("archived");
      }
    }
  }, [proposals, hasUserSelectedTab, bookedProposals.length, activeProposals.length, archivedProposals.length]);

  // Fetch Proposals when eventId changes
  useEffect(() => {
    const fetchProposals = async () => {
      try {
        setProposalsLoading(true);
        const res = await axiosInstance.get(`/events/${eventId}/booking-proposals?status=all`);
        setProposals(res.data.proposals || []);
      } catch (err) {
        console.error("Failed to load booking proposals", err);
      } finally {
        setProposalsLoading(false);
      }
    };

    if (eventId) {
      fetchProposals();
    }
  }, [eventId]);

  // Fetch Booking Details depending on role
  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        setLoadingDetails(true);
        setDetailsError("");
        if (isProfessional) {
          const res = await axiosInstance.get(`/bookings/professional/event/${eventId}`);
          console.log("[DEBUG] Professional Booking Details API Response (/api/bookings/professional/event/" + eventId + "):\n", JSON.stringify(res.data, null, 2));
          setBookingDetails(res.data.data || {});
        } else {
          const res = await axiosInstance.get(`/payments/customer/event/${eventId}`);
          console.log("[DEBUG] Customer Booking Details API Response:", JSON.stringify(res.data, null, 2));
          setBookingDetails(res.data.data || {});
        }
      } catch (err) {
        console.error("Failed to load booking details:", err);
        setDetailsError(err.response?.data?.message || err.message || "Failed to load booking details");
      } finally {
        setLoadingDetails(false);
      }
    };

    if (eventId) {
      fetchBookingDetails();
    }
  }, [eventId, isProfessional]);

  const handleBookingRedirect = (proposalIds) => {
    if (eventId) {
      sessionStorage.setItem(`selected_proposals_${eventId}`, JSON.stringify(proposalIds));
    }
    navigate(`/event/${eventId}/booking-summary`);
  };

  const handleRejectProposal = async (proposalId) => {
    try {
      await updateProposalStatus(proposalId, "rejected");
      setProposals((prev) =>
        prev.map((p) => (p._id === proposalId ? { ...p, status: "rejected" } : p))
      );
      setSelectedProposalIds((prev) => prev.filter((id) => id !== proposalId));
    } catch (err) {
      console.error("Failed to reject proposal:", err);
      showError(err?.message || "Failed to reject proposal");
    }
  };

  const handleToggleSelect = (proposal) => {
    if (isClosed) return;
    const id = proposal._id;
    const isSelected = selectedProposalIds.includes(id);
    if (isSelected) {
      setSelectedProposalIds((prev) => prev.filter((item) => item !== id));
    } else {
      if (selectedProposalIds.length === 0) {
        setSelectedProposalIds([id]);
      } else {
        const firstSelectedId = selectedProposalIds[0];
        const firstSelected = proposals.find((p) => p._id === firstSelectedId);
        if (firstSelected && firstSelected.paymentPlan !== proposal.paymentPlan) {
          showError(
            "Professionals with different payment plans can't be booked together. Please select proposals with the same payment plan."
          );
          return;
        }
        setSelectedProposalIds((prev) => [...prev, id]);
      }
    }
  };

  const currentProposals = isProfessional
    ? proposals
    : activeTab === "booked"
    ? bookedProposals
    : activeTab === "active"
    ? activeProposals
    : archivedProposals;

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 sm:pt-28 flex justify-center items-center">
        <div className="w-10 h-10 border-4 border-[#FF4D12] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white pt-24 sm:pt-28 flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0-8v6m0 5h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500 max-w-md mb-6">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2.5 rounded-xl bg-[#FF4D12] text-white hover:bg-[#e64415] transition-colors font-semibold"
        >
          Go Back
        </button>
      </div>
    );
  }

  const hasTopPadding = !isSidebarMode && !onBack;

  return (
    <div className={`booking-overview-page ${hasTopPadding ? 'min-h-screen bg-white pt-20 sm:pt-24 pb-12' : 'pt-0 pb-6 bg-white'}`}>
      <div className={`${isSidebarMode ? 'px-0 py-0' : 'max-w-7xl mx-auto px-2.5 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-6'} relative`}>
        
        {/* Back Link Header */}
        {!isSidebarMode && (
          <button
            onClick={onBack || (() => navigate(-1))}
            className="flex items-center gap-2 text-gray-500 hover:text-[#FF4D12] font-semibold text-sm mb-4 sm:mb-6 transition cursor-pointer"
          >
            <ArrowLeft size={16} />
            {isProfessional ? "Back to Event List" : "Back to Booking List"}
          </button>
        )}

        {!isSidebarMode && (
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-black mb-4 sm:mb-8">Booking Overview</h1>
        )}

        {/* Column Grid layout */}
        <div className={`grid grid-cols-1 ${isSidebarMode ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-3.5 sm:gap-6 mb-8 sm:mb-10 items-stretch`}>
          <div className={`${isSidebarMode ? 'col-span-1' : 'lg:col-span-2'} ${isSidebarMode ? 'order-2' : 'order-2 lg:order-1'} h-full flex flex-col`}>
            <BookingEventDetails eventId={eventId} onEventLoaded={setEventData} />
          </div>
          <div className={`${isSidebarMode ? 'col-span-1' : 'lg:col-span-1'} ${isSidebarMode ? 'order-1' : 'order-1 lg:order-2'} h-full flex flex-col`}>
            <BookingDetailsCard
              overview={bookingDetails}
              isProfessional={isProfessional}
              proposals={isProfessional ? proposals : bookedProposals}
              loading={loadingDetails}
              eventId={eventId}
              eventData={eventData}
            />
          </div>
        </div>

        {/* Proposals Section */}
        <div className="mt-8 sm:mt-12">
          {isProfessional ? (
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-black mb-3 sm:mb-4">
              My Proposal
            </h2>
          ) : (
            <div className="border-b border-gray-100 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex gap-4 sm:gap-8">
                <button
                  type="button"
                  onClick={() => {
                    setHasUserSelectedTab(true);
                    setActiveTab("booked");
                  }}
                  className={`pb-3 text-sm sm:text-base font-bold px-1 transition-all flex items-center gap-2 cursor-pointer ${
                    activeTab === "booked"
                      ? "text-[#FF4D12] border-b-2 border-[#FF4D12]"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Booked
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    activeTab === "booked" ? "bg-orange-100 text-[#FF4D12]" : "bg-gray-100 text-gray-500"
                  }`}>
                    {bookedProposals.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setHasUserSelectedTab(true);
                    setActiveTab("active");
                  }}
                  className={`pb-3 text-sm sm:text-base font-bold px-1 transition-all flex items-center gap-2 cursor-pointer ${
                    activeTab === "active"
                      ? "text-[#FF4D12] border-b-2 border-[#FF4D12]"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Active
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    activeTab === "active" ? "bg-orange-100 text-[#FF4D12]" : "bg-gray-100 text-gray-500"
                  }`}>
                    {activeProposals.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setHasUserSelectedTab(true);
                    setActiveTab("archived");
                  }}
                  className={`pb-3 text-sm sm:text-base font-bold px-1 transition-all flex items-center gap-2 cursor-pointer ${
                    activeTab === "archived"
                      ? "text-[#FF4D12] border-b-2 border-[#FF4D12]"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Archived
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    activeTab === "archived" ? "bg-orange-100 text-[#FF4D12]" : "bg-gray-100 text-gray-500"
                  }`}>
                    {archivedProposals.length}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Proposals Grid list using customize ProposalCard */}
          {proposalsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-8 h-8 border-3 border-[#FF4D12] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : currentProposals.length > 0 ? (
            <div className={
              isProfessional
                ? "flex flex-wrap gap-2.5 sm:gap-4 lg:gap-5"
                : "grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-5 lg:gap-6 items-stretch"
            }>
              {currentProposals.map((proposal) => {
                const isBooked = isProposalBooked(proposal);
                const isRejected = proposal.status === "rejected" || String(proposal?.status || "").toLowerCase() === "rejected";
                const isWithdrawn = proposal.status === "withdrawn" || String(proposal?.status || "").toLowerCase() === "withdrawn";
                const isCancelled = isProposalCancelled(proposal) && !isRejected && !isWithdrawn;
                const showCheckbox = !isProfessional && activeTab === "active" && !isClosed && proposal.status === "pending";
                const isSelected = selectedProposalIds.includes(proposal._id);

                return (
                  <ProposalCard
                    key={proposal._id}
                    proposal={proposal}
                    isProfessionalView={isProfessional}
                    customWidth={isProfessional ? "w-full max-w-[175px] sm:max-w-[205px] md:max-w-[225px] lg:max-w-[245px] xl:max-w-[255px]" : ""}
                    isBooked={isBooked}
                    isCancelled={isCancelled}
                    showCheckbox={showCheckbox}
                    isSelected={isSelected}
                    onToggleSelect={handleToggleSelect}
                    onReject={!isProfessional && activeTab === "active" ? handleRejectProposal : undefined}
                    onViewDetails={() => setSelectedProposalForModal(proposal)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 sm:py-16 bg-gray-50/50 rounded-2xl border border-gray-100 text-gray-400 text-sm sm:text-base">
              {isProfessional
                ? "No proposals found for this event."
                : activeTab === "booked"
                ? "No booked proposals for this event."
                : activeTab === "active"
                ? "No active proposals available for booking."
                : "No archived proposals found."}
            </div>
          )}

          {/* Selected proposals footer count and Continue button - exactly matching /event/:id */}
          {!isProfessional && activeTab === "active" && !isClosed && selectedProposalIds.length > 0 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-6 mb-8">
              <span className="text-sm font-semibold text-gray-600">
                {selectedProposalIds.length} {selectedProposalIds.length === 1 ? "proposal" : "proposals"} selected
              </span>
              <button
                type="button"
                onClick={() => handleBookingRedirect(selectedProposalIds)}
                className="bg-[#FF4D12] hover:bg-[#e64510] text-white px-6 py-2.5 rounded-xl font-bold transition-all shadow-md active:scale-95 flex items-center gap-2 text-sm cursor-pointer"
              >
                Continue →
              </button>
            </div>
          )}
        </div>

        {/* Detailed Modal */}
        {selectedProposalForModal && (
          <ProposalDetailedModal
            isOpen={!!selectedProposalForModal}
            onClose={() => setSelectedProposalForModal(null)}
            proposal={selectedProposalForModal}
            isProfessionalView={isProfessional}
            hostName={isProfessional ? selectedProposalForModal.companyName : undefined}
            hostAvatar={isProfessional ? selectedProposalForModal.profilePhoto : undefined}
            isBooked={isProposalBooked(selectedProposalForModal)}
            hideBookNow={isProfessional || activeTab !== "active" || isClosed || isProposalCancelled(selectedProposalForModal) || isProposalBooked(selectedProposalForModal)}
            onBookNow={(pid) => {
              setSelectedProposalForModal(null);
              handleBookingRedirect([pid]);
            }}
          />
        )}

      </div>
    </div>
  );
}
